import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, User, Users, Mail, Phone, LogOut, Heart, Plus, XCircle, AlertTriangle, Star, MessageSquare, Camera, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReviewDialog from '../components/ReviewDialog';
import EditPersonsDialog from '../components/EditPersonsDialog';
import { locationsAPI, bookingsAPI, paymentsAPI, reviewsAPI, locationsManagementAPI, favoritesAPI, authAPI } from '../utils/api';
import { toast } from 'sonner';

const Dashboard = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [myLocations, setMyLocations] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [hostApprovals, setHostApprovals] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = localStorage.getItem('currentUser');
    if (!user) {
      navigate('/login');
      return;
    }
    const parsedUser = JSON.parse(user);
    setCurrentUser(parsedUser);
    fetchDashboardData(parsedUser);
  }, [navigate]);

  const fetchDashboardData = async (user) => {
    try {
      setLoading(true);
      const [bookingsResponse, locationsResponse] = await Promise.all([
        bookingsAPI.getByUser(user.id),
        locationsAPI.getByUser(user.id),
      ]);
      setUserBookings(bookingsResponse.data);
      setMyLocations(locationsResponse.data);

      try {
        const pending = await reviewsAPI.getMyPending();
        setPendingReviews(pending.data);
      } catch (err) {
        console.error('Pending reviews error:', err);
      }

      try {
        const approvals = await bookingsAPI.getHostPendingApprovals();
        setHostApprovals(approvals.data);
      } catch (err) {
        console.error('Host approvals error:', err);
      }

      try {
        const favs = await favoritesAPI.list();
        setFavorites(favs.data);
      } catch (err) {
        console.error('Favorites error:', err);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    toast.success('Odhlášení úspěšné');
    navigate('/');
  };

  if (!currentUser) {
    return <div className="min-h-screen flex items-center justify-center">
      <p className="text-white text-xl">Načítání...</p>
    </div>;
  }

  const confirmedBookings = userBookings.filter(b => b.status === 'confirmed');
  const pendingBookings = userBookings.filter(b => b.status === 'pending');

  return (
    <div 
      className="min-h-screen"
    >
      <div className="relative">
        <Header />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <AvatarUploader
                    user={currentUser}
                    onChange={(updatedUser) => {
                      setCurrentUser(updatedUser);
                      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
                    }}
                  />
                  <h3 className="font-bold text-xl">{currentUser.name}</h3>
                  <p className="text-sm text-gray-500">
                    Členem od {currentUser.registered_date ? new Date(currentUser.registered_date).toLocaleDateString('cs-CZ') : '—'}
                  </p>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Mail className="w-4 h-4" />
                    <span>{currentUser.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Phone className="w-4 h-4" />
                    <span>{currentUser.phone}</span>
                  </div>
                </div>

                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="w-full mt-6"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Odhlásit se
                </Button>
              </CardContent>
            </Card>

            <Card className="shadow-lg mt-6">
              <CardContent className="p-6">
                <h3 className="font-bold mb-4">Rychlé akce</h3>
                <div className="space-y-2">
                  <Button
                    onClick={() => navigate('/locations')}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Najít místa
                  </Button>
                  <Button
                    onClick={() => navigate('/add-location')}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Přidat místo
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <h1 className="text-3xl font-bold mb-6">Můj Dashboard</h1>

            <Tabs defaultValue="bookings" className="space-y-6">
              <TabsList className="grid w-full max-w-4xl grid-cols-5">
                <TabsTrigger value="bookings" data-testid="tab-bookings">Rezervace</TabsTrigger>
                <TabsTrigger value="approvals" data-testid="tab-approvals">
                  Žádosti {hostApprovals.length > 0 && (
                    <Badge className="ml-2 bg-orange-600">{hostApprovals.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="my-locations" data-testid="tab-my-locations">
                  Moje lokality {myLocations.length > 0 && (
                    <Badge className="ml-2 bg-gray-500">{myLocations.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="reviews" data-testid="tab-reviews">
                  Hodnocení {pendingReviews.length > 0 && (
                    <Badge className="ml-2 bg-orange-600">{pendingReviews.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="favorites" data-testid="tab-favorites">
                  Oblíbené {favorites.length > 0 && (
                    <Badge className="ml-2 bg-red-500">{favorites.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="bookings" className="space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <StatCard
                    title="Celkem rezervací"
                    value={userBookings.length}
                    icon={<Calendar className="w-6 h-6" />}
                  />
                  <StatCard
                    title="Potvrzené"
                    value={confirmedBookings.length}
                    icon={<Calendar className="w-6 h-6" />}
                    color="green"
                  />
                  <StatCard
                    title="Čekající"
                    value={pendingBookings.length}
                    icon={<Calendar className="w-6 h-6" />}
                    color="yellow"
                  />
                </div>

                {/* Bookings List */}
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold">Vaše rezervace</h2>
                  {userBookings.length > 0 ? (
                    userBookings.map((booking) => (
                      <BookingCard
                        key={booking.id}
                        booking={booking}
                        onCancelled={() => fetchDashboardData(currentUser)}
                      />
                    ))
                  ) : (
                    <Card>
                      <CardContent className="p-12 text-center">
                        <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Žádné rezervace</h3>
                        <p className="text-gray-600 mb-6">
                          Ještě jste nevytvořili žádnou rezervaci
                        </p>
                        <Button
                          onClick={() => navigate('/locations')}
                          className="bg-orange-600 hover:bg-orange-700"
                        >
                          Prozkoumat místa
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="approvals" className="space-y-4">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-orange-500" />
                  Žádosti o rezervaci čekající na vaše schválení
                </h2>
                {hostApprovals.length > 0 ? (
                  hostApprovals.map((b) => (
                    <ApprovalCard
                      key={b.id}
                      booking={b}
                      onAction={() => fetchDashboardData(currentUser)}
                    />
                  ))
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Žádné žádosti</h3>
                      <p className="text-gray-600">
                        Až někdo požádá o rezervaci s povinným schválením, objeví se zde.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="my-locations" className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <MapPin className="w-6 h-6 text-orange-500" />
                    Moje lokality
                  </h2>
                  <Button
                    onClick={() => navigate('/add-location')}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="add-location-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Přidat novou lokalitu
                  </Button>
                </div>
                {myLocations.length > 0 ? (
                  myLocations.map((loc) => (
                    <MyLocationCard
                      key={loc.id}
                      location={loc}
                      onChanged={() => fetchDashboardData(currentUser)}
                    />
                  ))
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Žádné vlastní lokality</h3>
                      <p className="text-gray-600 mb-6">
                        Přidejte první lokalitu a začněte vydělávat!
                      </p>
                      <Button
                        onClick={() => navigate('/add-location')}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Přidat lokalitu
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="reviews" className="space-y-4">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Star className="w-6 h-6 text-orange-500" />
                  Hodnocení čekající na vás
                </h2>
                {pendingReviews.length > 0 ? (
                  pendingReviews.map((item) => (
                    <PendingReviewCard
                      key={`${item.booking.id}-${item.role}`}
                      item={item}
                      onSubmitted={() => fetchDashboardData(currentUser)}
                    />
                  ))
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Žádná čekající hodnocení</h3>
                      <p className="text-gray-600">
                        Jakmile skončí pobyt, objeví se zde výzva k hodnocení.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="favorites">
                {favorites.length === 0 ? (
                  <Card data-testid="favorites-empty">
                    <CardContent className="p-12 text-center">
                      <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Žádná oblíbená místa</h3>
                      <p className="text-gray-600 mb-6">
                        Začněte přidávat místa do oblíbených
                      </p>
                      <Button
                        onClick={() => navigate('/locations')}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        Prozkoumat místa
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="favorites-list">
                    {favorites.map(({ location, favorited_at }) => (
                      <Card
                        key={location.id}
                        className="overflow-hidden hover:shadow-xl transition-shadow"
                        data-testid={`favorite-card-${location.id}`}
                      >
                        <div
                          className="relative h-40 cursor-pointer"
                          onClick={() => navigate(`/location/${location.id}`)}
                        >
                          <img
                            src={location.photos?.[0] || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4'}
                            alt={location.name}
                            className="w-full h-full object-cover"
                          />
                          <Button
                            size="icon"
                            variant="secondary"
                            className="absolute top-2 right-2 bg-white/90 hover:bg-white"
                            data-testid={`remove-favorite-${location.id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await favoritesAPI.remove(location.id);
                                setFavorites((prev) => prev.filter((f) => f.location.id !== location.id));
                                toast.success('Odebráno z oblíbených');
                              } catch (err) {
                                toast.error('Nepodařilo se odebrat');
                              }
                            }}
                          >
                            <Heart className="w-4 h-4 fill-red-500 text-red-500" />
                          </Button>
                        </div>
                        <CardContent className="p-4">
                          <h4 className="font-bold mb-1 line-clamp-1">{location.name}</h4>
                          <p className="text-xs text-gray-500 line-clamp-2 mb-2">{location.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-orange-600">{location.price} Kč/noc</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/location/${location.id}`)}
                            >
                              Detail
                            </Button>
                          </div>
                          {favorited_at && (
                            <p className="text-[10px] text-gray-400 mt-2">
                              Uloženo {new Date(favorited_at).toLocaleDateString('cs-CZ')}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

const StatCard = ({ title, value, icon, color = 'orange' }) => {
  const colorClasses = {
    orange: 'bg-orange-100 text-orange-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-1">{title}</p>
            <p className="text-3xl font-bold">{value}</p>
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const BookingCard = ({ booking, onCancelled }) => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [refundPreview, setRefundPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const statusColors = {
    confirmed: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    pending_approval: 'bg-orange-100 text-orange-700',
    approved: 'bg-blue-100 text-blue-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  const statusLabels = {
    confirmed: 'Potvrzeno',
    pending: 'Čekající na platbu',
    pending_approval: 'Čeká na schválení',
    approved: 'Schváleno – zaplaťte',
    rejected: 'Odmítnuto majitelem',
    cancelled: 'Zrušeno',
  };

  const handleOpenCancel = async () => {
    setLoadingPreview(true);
    setDialogOpen(true);
    try {
      const response = await paymentsAPI.getRefundPreview(booking.id);
      setRefundPreview(response.data);
    } catch (error) {
      console.error('Refund preview error:', error);
      toast.error('Nepodařilo se načíst informace o vrácení');
      setDialogOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirmCancel = async () => {
    try {
      setCancelling(true);
      const response = await paymentsAPI.cancelBooking(booking.id);
      const data = response.data;
      if (data.refund_amount > 0) {
        toast.success(`Rezervace zrušena. Vráceno: ${data.refund_amount} Kč (${data.refund_percentage}%)`);
      } else {
        toast.success('Rezervace zrušena. Bez nároku na vrácení platby.');
      }
      setDialogOpen(false);
      if (onCancelled) onCancelled();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.response?.data?.detail || 'Chyba při rušení rezervace');
    } finally {
      setCancelling(false);
    }
  };

  const canCancel = !['cancelled', 'rejected'].includes(booking.status);
  const isPast = booking.check_out && new Date(booking.check_out) < new Date();
  const isFuture = booking.check_in && new Date(booking.check_in) > new Date();
  const canShowReview = isPast && !['cancelled', 'rejected'].includes(booking.status);
  // Guest can bump up person counts only on pre-approval per-unit bookings
  const canEditPersons = ['pending', 'pending_approval'].includes(booking.status) && (booking.adults != null || booking.cabin_persons != null);
  const [editPersonsOpen, setEditPersonsOpen] = useState(false);
  const canPayNow = booking.status === 'approved';

  const handlePayNow = async () => {
    const loadingId = toast.loading('Vytváření platby…');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ booking_id: booking.id, origin_url: window.location.origin }),
      });
      // Read body once as text, then JSON-parse defensively
      const raw = await response.text();
      let data = null;
      try { data = raw ? JSON.parse(raw) : null; } catch { data = null; }
      toast.dismiss(loadingId);
      if (!response.ok) {
        const msg = (data && data.detail) ? data.detail : `Chyba ${response.status}: ${raw.slice(0, 120)}`;
        throw new Error(msg);
      }
      if (!data || !data.checkout_url) throw new Error('Server nevrátil platební URL');
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.dismiss(loadingId);
      console.error('[handlePayNow] error:', e);
      toast.error(e.message || 'Chyba při vytváření platby');
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow" data-testid={`booking-card-${booking.id}`}>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="w-full md:w-48 h-32 rounded-lg bg-gradient-to-br from-orange-100 to-orange-300 flex items-center justify-center cursor-pointer"
               onClick={() => navigate(`/location/${booking.location_id}`)}>
            <MapPin className="w-12 h-12 text-orange-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
              <div>
                <h3 className="text-xl font-bold mb-1">{booking.location_name}</h3>
                <Badge className={statusColors[booking.status]}>
                  {statusLabels[booking.status]}
                </Badge>
                {booking.refund_amount > 0 && (
                  <Badge className="ml-2 bg-blue-100 text-blue-700">
                    Vráceno: {booking.refund_amount} Kč
                  </Badge>
                )}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-orange-600">{booking.total_price} Kč</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
              <div>
                <p className="text-gray-500">Příjezd</p>
                <p className="font-semibold">{booking.check_in}</p>
              </div>
              <div>
                <p className="text-gray-500">Odjezd</p>
                <p className="font-semibold">{booking.check_out}</p>
              </div>
              <div>
                <p className="text-gray-500">Hosté</p>
                <p className="font-semibold">{booking.guests} osob</p>
              </div>
              <div>
                <p className="text-gray-500">Vytvořeno</p>
                <p className="font-semibold">
                  {booking.created_at ? new Date(booking.created_at).toLocaleDateString('cs-CZ') : '—'}
                </p>
              </div>
            </div>

            {canPayNow && (
              <Button
                size="sm"
                onClick={handlePayNow}
                className="bg-orange-600 hover:bg-orange-700 text-white mr-2 font-bold shadow-md"
                data-testid={`pay-now-${booking.id}`}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Zaplatit {booking.total_price} Kč
              </Button>
            )}
            {canCancel && isFuture && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenCancel}
                className="text-red-600 border-red-300 hover:bg-red-50 mr-2"
                data-testid={`cancel-booking-${booking.id}`}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Zrušit rezervaci
              </Button>
            )}
            {canShowReview && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReviewOpen(true)}
                className="text-orange-600 border-orange-300 hover:bg-orange-50 mr-2"
                data-testid={`review-booking-${booking.id}`}
              >
                <Star className="w-4 h-4 mr-2" />
                Hodnocení
              </Button>
            )}
            {canEditPersons && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditPersonsOpen(true)}
                className="text-blue-600 border-blue-300 hover:bg-blue-50 mr-2"
                data-testid={`edit-persons-${booking.id}`}
              >
                <Users className="w-4 h-4 mr-2" />
                Upravit počet osob
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/my-booking/${booking.id}`)}
              data-testid={`booking-detail-${booking.id}`}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Detail & kontakt
            </Button>
          </div>
        </div>
      </CardContent>

      <ReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        booking={booking}
        onSubmitted={onCancelled}
      />

      <EditPersonsDialog
        open={editPersonsOpen}
        onOpenChange={setEditPersonsOpen}
        booking={booking}
        onUpdated={onCancelled}
      />

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent data-testid="cancel-booking-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Zrušit rezervaci?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                {loadingPreview ? (
                  <p>Načítání…</p>
                ) : refundPreview ? (
                  <>
                    <p>
                      Rezervace pro <strong>{booking.location_name}</strong> ({booking.check_in} → {booking.check_out})
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2 border">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Celková cena:</span>
                        <span className="font-semibold">{refundPreview.total_price} Kč</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Politika:</span>
                        <span className="text-sm">{refundPreview.policy_reason}</span>
                      </div>
                      <div className="flex justify-between text-lg pt-2 border-t">
                        <span className="font-semibold">Vrátíme vám:</span>
                        <span className={`font-bold ${refundPreview.refund_amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {refundPreview.refund_amount} Kč ({refundPreview.refund_percentage}%)
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Storno podmínky: 7+ dní 100% • 3–7 dní 50% • &lt;3 dny 0%
                    </p>
                  </>
                ) : (
                  <p>Nelze načíst informace.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Zpět</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              disabled={cancelling || loadingPreview}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-booking"
            >
              {cancelling ? 'Rušení…' : 'Potvrdit zrušení'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

const ApprovalCard = ({ booking, onAction }) => {
  const [rejecting, setRejecting] = useState(false);
  const [acting, setActing] = useState(false);
  const [reason, setReason] = useState('');

  const deadline = booking.approval_deadline
    ? new Date(booking.approval_deadline)
    : null;
  const hoursLeft = deadline
    ? Math.max(0, Math.round((deadline - new Date()) / 36e5))
    : null;

  const handleApprove = async () => {
    try {
      setActing(true);
      await bookingsAPI.approve(booking.id);
      toast.success('Rezervace schválena. Host byl informován emailem.');
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    try {
      setActing(true);
      await bookingsAPI.reject(booking.id, reason);
      toast.success('Rezervace odmítnuta. Host byl informován emailem.');
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    } finally {
      setActing(false);
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow border-orange-300" data-testid={`approval-card-${booking.id}`}>
      <CardContent className="p-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <Badge className="bg-orange-100 text-orange-700 mb-2">Žádost o rezervaci</Badge>
              <h3 className="text-xl font-bold">{booking.location_name}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {booking.check_in} → {booking.check_out} • {booking.guests} osob
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-orange-600">{booking.total_price} Kč</p>
              {hoursLeft !== null && (
                <p className="text-xs text-red-600 mt-1">
                  ⏰ Zbývá ~{hoursLeft} h na schválení
                </p>
              )}
            </div>
          </div>

          {!rejecting ? (
            <div className="flex gap-3 flex-wrap">
              <Button
                onClick={handleApprove}
                disabled={acting}
                className="bg-green-600 hover:bg-green-700"
                data-testid={`approve-${booking.id}`}
              >
                ✅ Schválit
              </Button>
              <Button
                variant="outline"
                onClick={() => setRejecting(true)}
                disabled={acting}
                className="text-red-600 border-red-300 hover:bg-red-50"
                data-testid={`reject-${booking.id}`}
              >
                ❌ Odmítnout
              </Button>
            </div>
          ) : (
            <div className="space-y-3 bg-red-50 rounded-lg p-4 border border-red-200">
              <label className="text-sm font-medium">Důvod odmítnutí (volitelné)</label>
              <textarea
                className="w-full border rounded-md p-2 text-sm"
                rows={2}
                placeholder="Termín je již rezervován / pozemek bude rekonstruován / …"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid={`reject-reason-${booking.id}`}
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleReject}
                  disabled={acting}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid={`confirm-reject-${booking.id}`}
                >
                  Potvrdit odmítnutí
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { setRejecting(false); setReason(''); }}
                  disabled={acting}
                >
                  Zpět
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};


const LocationBookingsTable = ({ locationId, locationName }) => {
  const [open, setOpen] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && bookings === null) {
      setLoading(true);
      try {
        const res = await bookingsAPI.getByLocation(locationId);
        setBookings(res.data || []);
      } catch {
        setBookings([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const STATUS_LABEL = {
    pending: 'Čeká na platbu',
    pending_approval: 'Čeká na schválení',
    approved: 'Schváleno – nezaplaceno',
    confirmed: 'Potvrzeno (zaplaceno)',
    rejected: 'Odmítnuto',
    cancelled: 'Zrušeno',
  };
  const STATUS_CLS = {
    pending: 'bg-yellow-100 text-yellow-700',
    pending_approval: 'bg-orange-100 text-orange-700',
    approved: 'bg-blue-100 text-blue-700',
    confirmed: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-600',
  };

  const sorted = (bookings || []).slice().sort((a, b) => (a.check_in || '').localeCompare(b.check_in || ''));

  return (
    <div className="mt-4 pt-4 border-t">
      <button
        type="button"
        onClick={toggleOpen}
        className="text-sm font-semibold text-orange-600 hover:text-orange-700 flex items-center gap-1"
        data-testid={`toggle-bookings-${locationId}`}
      >
        {open ? '▼' : '▶'} Rezervace tohoto místa
        {bookings !== null && <span className="text-gray-500 font-normal">({bookings.length})</span>}
      </button>
      {open && (
        <div className="mt-3 overflow-x-auto">
          {loading ? (
            <p className="text-sm text-gray-500">Načítám…</p>
          ) : sorted.length === 0 ? (
            <p className="text-sm text-gray-500 bg-gray-50 rounded p-3">Žádné rezervace na tomto místě.</p>
          ) : (
            <table className="w-full text-xs sm:text-sm" data-testid={`bookings-table-${locationId}`}>
              <thead className="bg-gray-50 border-b">
                <tr className="text-left">
                  <th className="px-2 py-2">Host</th>
                  <th className="px-2 py-2">Kontakt</th>
                  <th className="px-2 py-2">Od</th>
                  <th className="px-2 py-2">Do</th>
                  <th className="px-2 py-2">Osob</th>
                  <th className="px-2 py-2">Cena</th>
                  <th className="px-2 py-2">Stav</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(b => (
                  <tr key={b.id} className="border-b hover:bg-gray-50" data-testid={`booking-row-${b.id}`}>
                    <td className="px-2 py-2 font-medium">{b.guest_name || '—'}</td>
                    <td className="px-2 py-2 text-gray-600">
                      {b.guest_email && <div>{b.guest_email}</div>}
                      {b.guest_phone && <div className="text-xs">{b.guest_phone}</div>}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">{b.check_in}</td>
                    <td className="px-2 py-2 whitespace-nowrap">{b.check_out}</td>
                    <td className="px-2 py-2 text-center">{b.guests || b.adults || '—'}</td>
                    <td className="px-2 py-2 whitespace-nowrap font-semibold">{b.total_price} Kč</td>
                    <td className="px-2 py-2">
                      <Badge className={STATUS_CLS[b.status] || 'bg-gray-100 text-gray-600'}>
                        {STATUS_LABEL[b.status] || b.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};


const MyLocationCard = ({ location, onChanged }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    locationsManagementAPI.getStats(location.id).then(r => {
      if (mounted) setStats(r.data);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [location.id]);

  const handleToggle = async () => {
    try {
      setBusy(true);
      await locationsManagementAPI.toggleActive(location.id);
      toast.success(location.is_active === false ? 'Lokalita aktivována' : 'Lokalita pozastavena');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Opravdu smazat lokalitu "${location.name}"? Tato akce je nevratná.`)) return;
    try {
      setBusy(true);
      await locationsManagementAPI.delete(location.id);
      toast.success('Lokalita smazána');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    } finally { setBusy(false); }
  };

  const isActive = location.is_active !== false;

  return (
    <Card className="hover:shadow-lg transition-shadow" data-testid={`my-location-${location.id}`}>
      <CardContent className="p-5">
        <div className="flex flex-col md:flex-row gap-5">
          <img
            src={location.photos?.[0] || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4'}
            alt={location.name}
            className="w-full md:w-44 h-32 rounded-lg object-cover cursor-pointer"
            onClick={() => navigate(`/location/${location.id}`)}
          />
          <div className="flex-1">
            <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
              <div>
                <h3 className="text-xl font-bold">{location.name}</h3>
                <div className="flex gap-2 mt-1 flex-wrap">
                  <Badge variant="outline">{location.category}</Badge>
                  {location.instant_book === false ? (
                    <Badge className="bg-yellow-100 text-yellow-800">🔐 Schválení</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-800">⚡ Okamžitě</Badge>
                  )}
                  {isActive ? (
                    <Badge className="bg-blue-100 text-blue-700">Aktivní</Badge>
                  ) : (
                    <Badge className="bg-gray-200 text-gray-700">Pozastaveno</Badge>
                  )}
                </div>
              </div>
              <p className="text-2xl font-bold text-orange-600">{location.price} Kč<span className="text-sm font-normal">/noc</span></p>
            </div>

            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm bg-gray-50 rounded-lg p-3 my-3">
                <div>
                  <p className="text-gray-500 text-xs">Rezervací</p>
                  <p className="font-semibold">{stats.total_bookings}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Potvrzených</p>
                  <p className="font-semibold text-green-600">{stats.confirmed_bookings}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Hodnocení</p>
                  <p className="font-semibold">
                    {stats.average_rating ? `${stats.average_rating}★ (${stats.review_count})` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Vaše výplata (85%)</p>
                  <p className="font-semibold text-orange-600">{stats.host_payout_estimated} Kč</p>
                </div>
              </div>
            )}

            <div className="flex gap-2 flex-wrap mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/location/${location.id}`)}
                data-testid={`view-location-${location.id}`}
              >
                Zobrazit
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/edit-location/${location.id}`)}
                data-testid={`edit-location-${location.id}`}
              >
                ✏️ Upravit
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/manage-events/${location.id}`)}
                data-testid={`manage-events-${location.id}`}
              >
                📅 Akce
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/owner-calendar/${location.id}`)}
                data-testid={`owner-calendar-${location.id}`}
              >
                🗓️ Kalendář
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleToggle}
                disabled={busy}
                data-testid={`toggle-active-${location.id}`}
              >
                {isActive ? '⏸️ Pozastavit' : '▶️ Aktivovat'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDelete}
                disabled={busy}
                className="text-red-600 border-red-300 hover:bg-red-50"
                data-testid={`delete-location-${location.id}`}
              >
                🗑️ Smazat
              </Button>
            </div>

            {/* Expandable bookings table */}
            <LocationBookingsTable locationId={location.id} locationName={location.name} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PendingReviewCard = ({ item, onSubmitted }) => {
  const [open, setOpen] = useState(false);
  const { booking, role, days_remaining } = item;
  const label = role === 'guest' ? 'Ohodnotit místo' : 'Ohodnotit hosta';

  return (
    <Card className="hover:shadow-lg transition-shadow border-orange-200" data-testid={`pending-review-${booking.id}`}>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className="bg-orange-100 text-orange-700">
                {role === 'guest' ? 'Jste host' : 'Jste pronajímatel'}
              </Badge>
              <Badge variant="outline">
                Zbývá {days_remaining} {days_remaining === 1 ? 'den' : 'dní'}
              </Badge>
            </div>
            <h3 className="text-lg font-bold mb-1">{booking.location_name}</h3>
            <p className="text-sm text-gray-600">
              {booking.check_in} → {booking.check_out} • {booking.guests} osob
            </p>
          </div>
          <Button
            onClick={() => setOpen(true)}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid={`open-review-${booking.id}`}
          >
            <Star className="w-4 h-4 mr-2" />
            {label}
          </Button>
        </div>
      </CardContent>
      <ReviewDialog open={open} onOpenChange={setOpen} booking={booking} onSubmitted={onSubmitted} />
    </Card>
  );
};

// Owner avatar uploader (also reused for guest profile photo)
const AvatarUploader = ({ user, onChange }) => {
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);

  const compress = (file) => new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) return reject(new Error('Není to obrázek'));
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const max = 400; // square avatar source side
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;
        const canvas = document.createElement('canvas');
        canvas.width = max; canvas.height = max;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, sx, sy, side, side, 0, 0, max, max);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch (e) { URL.revokeObjectURL(url); reject(e); }
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await compress(file);
      const res = await authAPI.updateAvatar(dataUrl);
      onChange(res.data);
      toast.success('Profilová fotka uložena');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nahrání selhalo');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onRemove = async () => {
    setBusy(true);
    try {
      const res = await authAPI.updateAvatar(null);
      onChange(res.data);
      toast.success('Profilová fotka odstraněna');
    } catch (err) {
      toast.error('Smazání selhalo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-3 flex flex-col items-center" data-testid="avatar-uploader">
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="relative group w-24 h-24 rounded-full overflow-hidden bg-orange-600 hover:opacity-90 transition shadow-lg border-4 border-white"
        aria-label="Nahrát profilovou fotku"
        data-testid="avatar-upload-btn"
      >
        {user?.avatar ? (
          <img src={user.avatar} alt="Profilová fotka" className="w-full h-full object-cover" />
        ) : (
          <User className="w-12 h-12 text-white m-auto" />
        )}
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition">
          <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100" />
        </span>
      </button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
      {user?.avatar && (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="text-xs text-gray-500 hover:text-red-600 mt-1"
        >
          Odstranit fotku
        </button>
      )}
    </div>
  );
};

export default Dashboard;
